import { type VcsJoinPolicy } from 'consts';
import {
    AccountSettingsControllerService,
    VcsChangeJoinApprovalPolicyRequest,
    VcsChangeOnlyModeratorsCanApproveWaitingUsersRequest,
    VcsMessagingControllerService,
    VcsWaitingRoomRequest,
} from 'api';
import { getRoomNameAndWorkspaceId } from 'useCase/moderation/utils/moderatorApi';
import { shouldUseMeetVcsOAuthApi } from 'useCase/moderation/utils/vcsApiRouting';
import { getLogger, LOGGERS } from 'lib/logger';

const logger = getLogger(LOGGERS.AccessRequests);

export type WaitingOperation = 'APPROVE' | 'REJECT';

const APPROVE_WAITING_TIMEOUT_MS = 8000;

type ApiOptions = { signal?: AbortSignal };

interface WorkspaceAwareApi<TBody, TResult> {
    workspace: (workspaceId: number, body: TBody, options?: ApiOptions) => Promise<TResult>;
    oauth: (body: TBody, options?: ApiOptions) => Promise<TResult>;
}

const callWorkspaceAwareApi = <TBody, TResult>(
    isUseOAuth: boolean,
    workspaceId: number | null | undefined,
    body: TBody,
    api: WorkspaceAwareApi<TBody, TResult>,
    options?: ApiOptions,
): Promise<TResult> => {
    return isUseOAuth ? api.oauth(body, options) : api.workspace(workspaceId as number, body, options);
};

export type ApproveWaitingUsersParams = {
    waitingListIds: string[];
    operation: WaitingOperation;
    jwtToken: string | null;
    isLocalGuest: boolean;
};

export const approveWaitingUsersApi = async ({
    waitingListIds,
    operation,
    jwtToken,
    isLocalGuest,
}: ApproveWaitingUsersParams): Promise<{ success: boolean }> => {
    const { roomName, workspaceId } = getRoomNameAndWorkspaceId();

    if (!roomName) {
        logger.warn('[Moderation] approveWaitingUsersApi: roomName отсутствует');
        return { success: false };
    }

    const isUseOAuth = shouldUseMeetVcsOAuthApi(jwtToken);

    if (!jwtToken) {
        logger.warn('[waiting-room] approveWaitingUsersApi: нет conference jwtToken', {
            roomName,
            isLocalGuest,
            workspaceId: workspaceId ?? 'нет',
        });
        return { success: false };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), APPROVE_WAITING_TIMEOUT_MS);

    const requestBody: VcsWaitingRoomRequest = {
        roomName,
        waitingListIds,
        operation,
        jwtToken,
    };

    try {
        const result = await callWorkspaceAwareApi(
            isUseOAuth,
            workspaceId,
            requestBody,
            {
                workspace: VcsMessagingControllerService.approveWaitingUser,
                oauth: VcsMessagingControllerService.approveWaitingUserOAuth,
            },
            { signal: controller.signal },
        );

        if (result?.error) {
            logger.warn('[Moderation] approveWaitingUsersApi: ошибка', result.error);
            return { success: false };
        }
        return { success: true };
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            logger.warn('[Moderation] approveWaitingUsersApi: timeout', { roomName, operation });
        } else {
            logger.warn('[Moderation] approveWaitingUsersApi: network error', error);
        }
        return { success: false };
    } finally {
        clearTimeout(timeoutId);
    }
};

export type CallJoinPolicy = VcsJoinPolicy;

export type SyncCallSettingsParams = {
    joinPolicy: CallJoinPolicy;
    moderatorsApprovalOnly: boolean;
    jwtToken: string | null;
    isLocalGuest: boolean;
};

export const syncCallSettingsApi = async (
    params: SyncCallSettingsParams,
): Promise<{ hasError: boolean; failedFields: string[] }> => {
    const { roomName, workspaceId } = getRoomNameAndWorkspaceId();
    const { jwtToken, isLocalGuest } = params;

    if (!roomName) {
        logger.warn('[Moderation] syncCallSettingsApi: roomName отсутствует');
        return { hasError: true, failedFields: ['joinPolicy', 'moderatorsApprovalOnly'] };
    }

    const isUseOAuth = shouldUseMeetVcsOAuthApi(jwtToken);

    if (!jwtToken) {
        logger.warn('[Moderation] syncCallSettingsApi: нет conference jwtToken', {
            isLocalGuest,
            workspaceId: workspaceId ?? 'нет',
        });
        return { hasError: true, failedFields: ['joinPolicy', 'moderatorsApprovalOnly'] };
    }

    const oauthJwt = { jwtToken };

    const joinPolicyRequest: VcsChangeJoinApprovalPolicyRequest = {
        roomName,
        joinApprovalPolicy: params.joinPolicy,
        ...oauthJwt,
    };

    const moderatorsRequest: VcsChangeOnlyModeratorsCanApproveWaitingUsersRequest = {
        roomName,
        onlyModeratorsCanApproveWaitingUsers: params.moderatorsApprovalOnly,
        ...oauthJwt,
    };

    const [joinPolicyResult, moderatorsResult] = await Promise.all([
        callWorkspaceAwareApi(isUseOAuth, workspaceId, joinPolicyRequest, {
            workspace: VcsMessagingControllerService.changeJoinApprovalPolicy,
            oauth: VcsMessagingControllerService.changeJoinApprovalPolicyOAuth,
        }),
        callWorkspaceAwareApi(isUseOAuth, workspaceId, moderatorsRequest, {
            workspace: VcsMessagingControllerService.changeOnlyModeratorsCanApproveWaitingUsers,
            oauth: VcsMessagingControllerService.changeOnlyModeratorsCanApproveWaitingUsersOAuth,
        }),
    ]);

    const failedFields: string[] = [];
    if (joinPolicyResult.error) {
        logger.warn('[Moderation] syncCallSettingsApi: ошибка joinPolicy', joinPolicyResult.error);
        failedFields.push('joinPolicy');
    }
    if (moderatorsResult.error) {
        logger.warn('[Moderation] syncCallSettingsApi: ошибка moderatorsApprovalOnly', moderatorsResult.error);
        failedFields.push('moderatorsApprovalOnly');
    }

    return { hasError: failedFields.length > 0, failedFields };
};

export const saveSoundSettingOAuth = async (
    soundEnabled: boolean,
): Promise<{ hasError: boolean; failedFields: string[] }> => {
    const { error } = await AccountSettingsControllerService.updateVcsAccessRequestsSoundOAuth({
        vcsAccessRequestsSoundEnabled: soundEnabled,
    });
    return { hasError: Boolean(error), failedFields: error ? ['sound'] : [] };
};

export const saveNotificationsSettingOAuth = async (
    notificationsEnabled: boolean,
): Promise<{ hasError: boolean; failedFields: string[] }> => {
    const { error } = await AccountSettingsControllerService.updateVcsAccessRequestsNotificationsOAuth({
        vcsAccessRequestsNotificationsEnabled: notificationsEnabled,
    });
    return { hasError: Boolean(error), failedFields: error ? ['notifications'] : [] };
};
